import React, { useState } from "react";
import { 
  FiCode, 
  FiServer, 
  FiList, 
  FiFileText, 
  FiTerminal,
  FiCopy,
  FiCheck,
  FiBookOpen
} from "react-icons/fi";

// Reusable Code Block Component with Copy Feature
const CodeBlock = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative bg-gray-900 rounded-xl overflow-hidden mt-4 shadow-lg border border-gray-800">
      <div className="flex justify-between items-center px-4 py-2 bg-gray-800 border-b border-gray-700">
        <span className="text-xs font-mono text-gray-400 uppercase">{language}</span>
        <button 
          onClick={handleCopy}
          className="text-gray-400 hover:text-white transition-colors flex items-center gap-1 text-xs"
        >
          {copied ? <><FiCheck className="text-[#C5F637]" /> Copied!</> : <><FiCopy /> Copy</>}
        </button>
      </div>
      <div className="p-4 overflow-x-auto">
        <pre className="text-sm text-gray-100 font-mono whitespace-pre">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );
};

export default function ApiReference() {
  const [activeTab, setActiveTab] = useState('nodejs');

  const parameters = [
    { name: "apiKey", type: "String", req: "Yes", desc: "Your unique API authentication key provided by Eurofox." },
    { name: "phoneNumber", type: "String", req: "Yes", desc: "The destination mobile number (e.g., 9876543210). Include country code if required by your account settings." },
    { name: "message", type: "String", req: "Yes", desc: "The content of the SMS. Must be URL-encoded. (e.g., replace spaces with + or %20)." },
    { name: "senderId", type: "String", req: "Yes", desc: "Your approved 6-character Sender ID (e.g., EURFOX)." },
    { name: "priority", type: "Integer", req: "Yes", desc: "Route priority. Use 1 for high-priority/transactional routes." },
    { name: "templateID", type: "String", req: "Yes", desc: "The DLT-approved Template ID associated with your message content." },
    { name: "peID", type: "String", req: "Yes", desc: "Your DLT Principal Entity (PE) ID." },
    { name: "scheduleTime", type: "String", req: "No", desc: "Used to schedule a message for a future date/time. Leave blank for immediate dispatch." },
  ];

  const codeExamples = {
    curl: `curl -X GET "https://api.eurofox.in/api/sms/send-url?apiKey=YOUR_API_KEY&phoneNumber=9876543210&message=Dear+2486+is+the+OTP+for+your+login+at+our+portal.+In+case+you+have+not+requested+this+,+please+contact+us&senderId=YOUR_SENDER_ID&priority=1&templateID=YOUR_TEMPLATE_ID&peID=YOUR_PE_ID&scheduleTime="`,
    nodejs: `const axios = require('axios');

const sendSMS = async () => {
  const url = 'https://api.eurofox.in/api/sms/send-url';
  
  // Axios will automatically URL-encode the parameters
  const params = {
    apiKey: 'YOUR_API_KEY',
    phoneNumber: '9876543210',
    message: 'Dear 2486 is the OTP for your login at our portal. In case you have not requested this, please contact us',
    senderId: 'YOUR_SENDER_ID',
    priority: 1,
    templateID: 'YOUR_TEMPLATE_ID',
    peID: 'YOUR_PE_ID',
    scheduleTime: ''
  };

  try {
    const response = await axios.get(url, { params });
    console.log('SMS sent successfully:', response.data);
  } catch (error) {
    console.error('Error sending SMS:', error);
  }
};

sendSMS();`,
    python: `import requests

url = "https://api.eurofox.in/api/sms/send-url"

# The requests library handles URL-encoding automatically
querystring = {
    "apiKey": "YOUR_API_KEY",
    "phoneNumber": "9876543210",
    "message": "Dear 2486 is the OTP for your login at our portal. In case you have not requested this, please contact us",
    "senderId": "YOUR_SENDER_ID",
    "priority": "1",
    "templateID": "YOUR_TEMPLATE_ID",
    "peID": "YOUR_PE_ID",
    "scheduleTime": ""
}

response = requests.get(url, params=querystring)
print(response.text)`,
    php: `<?php
$apiKey = "YOUR_API_KEY";
$phone = "9876543210";

// Raw string urlencode() handles the formatting
$raw_message = "Dear 2486 is the OTP for your login at our portal. In case you have not requested this, please contact us";
$message = urlencode($raw_message);

$senderId = "YOUR_SENDER_ID";
$templateId = "YOUR_TEMPLATE_ID";
$peId = "YOUR_PE_ID";

$url = "https://api.eurofox.in/api/sms/send-url?apiKey=$apiKey&phoneNumber=$phone&message=$message&senderId=$senderId&priority=1&templateID=$templateId&peID=$peId&scheduleTime=";

$curl = curl_init();

curl_setopt_array($curl, array(
  CURLOPT_URL => $url,
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_ENCODING => "",
  CURLOPT_MAXREDIRS => 10,
  CURLOPT_TIMEOUT => 30,
  CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,
  CURLOPT_CUSTOMREQUEST => "GET",
));

$response = curl_exec($curl);
$err = curl_error($curl);
curl_close($curl);

if ($err) {
  echo "CURL Error #:" . $err;
} else {
  echo $response;
}
?>`
  };

  return (
    <div className="min-h-screen bg-[#F0EFEB] p-6 font-sans">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Section (Matching Templates Page Style) */}
        <div className="mb-8 flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border border-[#0C4550]/30 shadow-sm">
                <FiCode className="text-[#0C4550] w-6 h-6" />
            </div>
            <div>
                <h1 className="text-3xl font-bold text-[#0C4550]">API Reference</h1>
                <p className="text-medium text-gray-600 mt-1 flex items-center gap-2">
                    Eurofox SMS API Integration Guide
                </p>
            </div>
        </div>

        {/* Content Area */}
        <div className="space-y-8 pb-20">
          
          {/* Overview Section */}
          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold text-[#0C4550] mb-4 flex items-center gap-2">
              <FiBookOpen className="text-[#C5F637] bg-[#0C4550] p-1 rounded-md" /> Overview
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Technical documentation for integrating the Eurofox SMS dispatch system. This guide provides the necessary details to integrate the Eurofox SMS API. This API allows developers to trigger transactional or promotional SMS messages programmatically from their applications via a simple HTTP GET request.
            </p>
          </section>

          {/* Endpoint Information Section */}
          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold text-[#0C4550] mb-6 flex items-center gap-2">
              <FiServer className="text-[#C5F637] bg-[#0C4550] p-1 rounded-md" /> Endpoint Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Base URL</p>
                <p className="font-mono text-[#0C4550] font-semibold break-all">https://api.eurofox.in</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Endpoint</p>
                <p className="font-mono text-[#0C4550] font-semibold">/api/sms/send-url</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Method</p>
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 font-bold text-sm rounded-lg border border-blue-200">GET</span>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Content-Type</p>
                <p className="font-mono text-[#0C4550] text-sm font-semibold">application/x-www-form-urlencoded</p>
              </div>
            </div>
          </section>

          {/* Parameters Section */}
          <section className="bg-white rounded-2xl p-0 border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-[#0C4550] mb-2 flex items-center gap-2">
                <FiList className="text-[#C5F637] bg-[#0C4550] p-1 rounded-md" /> Request Parameters
              </h2>
              <p className="text-gray-600">Pass the following parameters in the query string of your HTTP GET request.</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-[#0C4550]/5 text-xs font-bold text-[#0C4550] uppercase tracking-wider">
                  <tr>
                    <th className="py-4 px-6 border-b border-gray-200">Parameter</th>
                    <th className="py-4 px-4 border-b border-gray-200">Type</th>
                    <th className="py-4 px-4 border-b border-gray-200">Required</th>
                    <th className="py-4 px-6 border-b border-gray-200">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {parameters.map((param, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 font-mono text-sm font-bold text-[#0C4550]">{param.name}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{param.type}</td>
                      <td className="py-4 px-4">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${param.req === 'Yes' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                          {param.req}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700">{param.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Formatting Section */}
          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold text-[#0C4550] mb-4 flex items-center gap-2">
              <FiFileText className="text-[#C5F637] bg-[#0C4550] p-1 rounded-md" /> Formatting the Message Payload
            </h2>
            <p className="text-gray-700 mb-6">
              Because this is a GET request, the message string must be URL-encoded to ensure special characters and spaces are transmitted safely across the network.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-red-50 border border-red-100 rounded-xl p-5">
                <p className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2">Raw Message Example</p>
                <p className="font-mono text-sm text-red-900 break-words">
                  "Dear 2486 is the OTP for your login at our portal. In case you have not requested this, please contact us"
                </p>
              </div>
              <div className="bg-[#C5F637]/10 border border-[#C5F637]/30 rounded-xl p-5">
                <p className="text-xs font-bold text-[#0C4550] uppercase tracking-wider mb-2">URL-Encoded Example</p>
                <p className="font-mono text-sm text-[#0C4550] break-words">
                  Dear+2486+is+the+OTP+for+your+login+at+our+portal.+In+case+you+have+not+requested+this+,+please+contact+us
                </p>
              </div>
            </div>
          </section>

          {/* Code Examples Section */}
          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold text-[#0C4550] mb-2 flex items-center gap-2">
              <FiTerminal className="text-[#C5F637] bg-[#0C4550] p-1 rounded-md" /> Integration Examples
            </h2>
            <p className="text-gray-600 mb-6">Here are examples of how to construct and send the API request using common programming languages.</p>

            {/* Language Tabs */}
            <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200 pb-4">
              {[
                { id: 'nodejs', label: 'Node.js (Axios)' },
                { id: 'python', label: 'Python (Requests)' },
                { id: 'php', label: 'PHP (CURL)' },
                { id: 'curl', label: 'cURL (CLI)' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                    activeTab === tab.id 
                      ? 'bg-[#0C4550] text-[#C5F637] shadow-md' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.label.split(' [')[0]}
                </button>
              ))}
            </div>

            {/* Code Renderer */}
            <div className="animate-fadeIn">
               {activeTab === 'nodejs' && <CodeBlock code={codeExamples.nodejs} language="javascript" />}
               {activeTab === 'python' && <CodeBlock code={codeExamples.python} language="python" />}
               {activeTab === 'php' && <CodeBlock code={codeExamples.php} language="php" />}
               {activeTab === 'curl' && <CodeBlock code={codeExamples.curl} language="bash" />}
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}